import { useCallback, useEffect, useState } from "react";

// Toolkit Imports
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";

// 3rd Party Imports
import { TabView, TabPanel } from "primereact/tabview";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";

// Local Components
import ActionsTab from "@/components/champion/ActivityConfiguration/ActionsTab";
import ImpactsTab from "@/components/champion/ActivityConfiguration/ImpactsTab";
import MentorGuidesTab from "@/components/champion/ActivityConfiguration/MentorGuidesTab";
import CustomMessagingTab from "@/components/champion/ActivityConfiguration/CustomMessagingTab";

export function ActivityConfiguration() {
  useBreadcrumb({
    label: "Activity Configuration",
    route: AdminAppRoutes.routes.champions.routes.activities.route,
    display: "none",
    resetTrail: true,
  });

  const [activeIndex, setActiveIndex] = useState(0);
  const [unsavedTabs, setUnsavedTabs] = useState<Record<number, boolean>>({});

  const hasUnsavedChanges = Object.values(unsavedTabs).some(Boolean);

  const handleUnsavedChange = useCallback((tabIndex: number) => {
    const onChangeForTab = (hasChanges: boolean) => {
      setUnsavedTabs(prev => ({ ...prev, [tabIndex]: hasChanges }));
    };

    return onChangeForTab;
  }, []);

  // Guard browser close/refresh
  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const handler = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", handler);

    return () => window.removeEventListener("beforeunload", handler);
  }, [hasUnsavedChanges]);

  const handleTabChange = (newIndex: number) => {
    if (hasUnsavedChanges) {
      confirmDialog({
        message:
          "You have unsaved changes. Are you sure you want to switch tabs and lose those changes?",
        header: "Unsaved Changes",
        icon: "pi pi-exclamation-triangle",
        acceptLabel: "Yes, switch tabs",
        rejectLabel: "No, stay on this tab",
        accept: () => {
          setActiveIndex(newIndex);
          setUnsavedTabs({});
        },
      });
    } else {
      setActiveIndex(newIndex);
    }
  };

  return (
    <>
      <div className="mb-4">
        <h1 className="atlas-h2 mb-6">Configure Champion Passport Activities</h1>
      </div>
      <ConfirmDialog />
      <TabView
        aria-label="Activity configuration"
        activeIndex={activeIndex}
        onTabChange={e => handleTabChange(e.index)}
      >
        <TabPanel header="Actions & Objects">
          <ActionsTab onUnsavedChange={handleUnsavedChange(0)} />
        </TabPanel>
        <TabPanel header="Impacts">
          <ImpactsTab onUnsavedChange={handleUnsavedChange(1)} />
        </TabPanel>
        <TabPanel header="Mentor Guides">
          <MentorGuidesTab onUnsavedChange={handleUnsavedChange(2)} />
        </TabPanel>
        <TabPanel header="Custom Messaging">
          <CustomMessagingTab onUnsavedChange={handleUnsavedChange(3)} />
        </TabPanel>
      </TabView>
    </>
  );
}
